import { create } from "zustand";
import { axiosInstance } from "../lib/axios";
import toast from "react-hot-toast";

export type ClientTier = "Premium" | "Standard" | "Basic";
export type ClientContactType = "Agency" | "Individual";

export interface Client {
  _id: string;
  name: string;
  username: string;
  email?: string;
  phone?: string;
  website?: string;
  company?: string;
  leadStatus?: string;
  leadSource?: string;
  tier?: ClientTier;
  contactType?: ClientContactType;
  notes?: string;
  createdAt?: string;
  updatedAt?: string;
  password?:String
}

export interface ClientCredentials {
  name: string;
  email?: string;
  phone?: string;
  website?: string;
  company?: string;
  leadStatus?: string;
  leadSource?: string;
  tier?: ClientTier;
  contactType?: ClientContactType;
  notes?: string;
   password?:String
}

interface ClientState {
  clients: Client[];
  selectedClient: Client | null;
  isFetchingClients: boolean;
  isCreatingClient: boolean;
  isUpdatingClient: boolean;
  isDeletingClient: boolean;
  fetchClients: () => Promise<void>;
  createClient: (data: ClientCredentials) => Promise<any>;
  updateClient: (id: string, data: Partial<Client>) => Promise<void>;
  deleteClient: (id: string) => Promise<void>;
  setSelectedClient: (client: Client | null) => void;
  createClientAccount: (id: string, data: { email: string; password: string }) => Promise<void>;
  provisionPortal: (id: string) => Promise<{ status: string; emailSent: boolean; credentials: { portalUrl: string; username: string; tempPassword: string } | null } | null>;
  regeneratePortal: (id: string, sendEmail?: boolean) => Promise<{ credentials: { portalUrl: string; username: string; tempPassword: string }; emailSent: boolean } | null>;
}

export const useClientStore = create<ClientState>((set, get) => ({
  clients: [],
  selectedClient: null,
  isFetchingClients: false,
  isCreatingClient: false,
  isUpdatingClient: false,
  isDeletingClient: false,

  fetchClients: async () => {
    set({ isFetchingClients: true });
    try {
      const res = await axiosInstance.get<Client[]>("/clients");
      set({ clients: res.data });
    } catch (error: any) {
      console.error("Error fetching clients:", error);
      toast.error(error.response?.data?.message || "Failed to fetch clients");
    } finally {
      set({ isFetchingClients: false });
    }
  },

  createClient: async (data) => {
    set({ isCreatingClient: true });
    try {
      const res :any= await axiosInstance.post<Client>("/clients", data);
      set({ clients: [res.data.client, ...get().clients] });

      toast.success("Client created successfully");
      return res.data.client;
    } catch (error: any) {
      console.error("Error creating client:", error);
      toast.error(error.response?.data?.message || "Failed to create client");
      return null;
    } finally {
      set({ isCreatingClient: false });
    }
  },

  updateClient: async (id, data) => {
    set({ isUpdatingClient: true });
    try {
      const res:any = await axiosInstance.put<Client>(`/clients/${id}`, data);
      set({
        clients: get().clients.map((client) =>
          client._id === id ? res.data.client : client
        ),
      });
      toast.success("Client updated successfully");
    } catch (error: any) {
      console.error("Error updating client:", error);
      toast.error(error.response?.data?.message || "Failed to update client");
    } finally {
      set({ isUpdatingClient: false });
    }
  },

  createClientAccount: async (id, data) => {
  try {
    const res = await axiosInstance.post(`/clients/${id}/create-account`, data);
    set({
      clients: get().clients.map((client) =>
        client._id === id ? { ...client, ...res.data.client } : client
      ),
    });
    toast.success("Client account created successfully");
  } catch (error: any) {
    console.error("Error creating client account:", error);
    toast.error(error.response?.data?.message || "Failed to create client account");
  }
},

  provisionPortal: async (id) => {
    try {
      const res = await axiosInstance.post(`/clients/${id}/portal/provision`, {});
      toast.success("Portal provisioned");
      return res.data?.portalAccount || null;
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to provision portal");
      return null;
    }
  },

  regeneratePortal: async (id, sendEmail = true) => {
    try {
      const res = await axiosInstance.post(`/clients/${id}/portal/regenerate`, { sendEmail });
      toast.success(res.data?.emailSent ? "New credentials sent by email" : "Credentials regenerated");
      return { credentials: res.data?.credentials, emailSent: !!res.data?.emailSent };
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to regenerate credentials");
      return null;
    }
  },

  deleteClient: async (id) => {
    set({ isDeletingClient: true });
    try {
      await axiosInstance.delete(`/clients/${id}`);
      set({
        clients: get().clients.filter((client) => client._id !== id),
      });
      toast.success("Client deleted successfully");
    } catch (error: any) {
      console.error("Error deleting client:", error);
      toast.error(error.response?.data?.message || "Failed to delete client");
    } finally {
      set({ isDeletingClient: false });
    }
  },

  setSelectedClient: (client) => set({ selectedClient: client }),
}));
